import { Payment } from './payment.model';

export type TicketStatus = 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed';
export type TicketType = 'claim' | 'question' | 'fraud' | 'return' | 'report';

export const TICKET_STATUS_LABELS: Record<string, string> = {
  open: 'Abierto',
  in_progress: 'En progreso',
  escalated: 'Escalado',
  resolved: 'Resuelto',
  closed: 'Cerrado',
};

export const TICKET_TYPE_LABELS: Record<string, string> = {
  claim: 'Reclamo',
  question: 'Pregunta',
  fraud: 'Fraude',
  return: 'Devolución',
  report: 'Reporte',
};

/** Adjunto de un mensaje o evidencia de una devolución (ambos viven en Spaces). */
export interface EvidenceFile {
  url: string;
  name?: string;
}

/**
 * Extensiones que cualquier navegador pinta con un `<img>`.
 *
 * La subida de evidencias acepta además PDF y HEIC/HEIF (lo que manda un
 * iPhone si no convierte), que el navegador de escritorio NO sabe dibujar:
 * esos se quedan como enlace en vez de dejar un hueco roto.
 */
const INLINE_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.avif', '.gif'];

/** ¿Este adjunto se puede mostrar dentro del panel? */
export function isInlineImage(url: string | null | undefined): boolean {
  if (!url) return false;
  // La URL puede traer query string o ancla: la extensión va antes.
  const path = url.split(/[?#]/)[0].toLowerCase();
  return INLINE_IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

/** ¿Es un PDF? (para darle su propio icono en la lista de adjuntos). */
export function isPdfFile(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.split(/[?#]/)[0].toLowerCase().endsWith('.pdf');
}

/** Mensaje dentro de un ticket (adjuntos en Spaces). */
export interface TicketMessage {
  id?: number;
  ticketId?: number;
  senderUserId?: number;
  senderName?: string;
  isAdmin?: boolean | number;
  message?: string;
  attachments?: EvidenceFile[];
  createdAt?: string;
  [key: string]: unknown;
}

/** Ticket de soporte. */
export interface SupportTicket {
  id: number;
  userId?: number;
  userName?: string;
  userEmail?: string;
  type?: string;
  status?: string;
  subject?: string;
  paymentId?: number;
  level?: number;
  assignedTo?: number;
  resolution?: string;
  messages?: TicketMessage[];
  payment?: Partial<Payment>;
  createdAt?: string;
  updatedAt?: string;
  /** Resumen de la conversación (lo calcula el backend en la lista y el detalle). */
  lastMessage?: string;
  lastMessageAt?: string;
  lastMessageIsAdmin?: boolean | null;
  messagesCount?: number;
  /** Mensajes del usuario que soporte todavía no lee. */
  unreadCount?: number;
  [key: string]: unknown;
}

/** Investigación de fraude. */
export interface FraudReport {
  id: number;
  userId?: number;
  paymentId?: number;
  ticketId?: number;
  status?: string;
  outcome?: string;
  notes?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** Entrada de la lista negra. */
export interface BlacklistEntry {
  id: number;
  email?: string;
  rfc?: string;
  clabe?: string;
  phone?: string;
  reason?: string;
  createdAt?: string;
  [key: string]: unknown;
}

export type ReturnStatus = 'requested' | 'approved' | 'in_transit' | 'received' | 'resolved' | 'rejected';

export const RETURN_STATUS_LABELS: Record<string, string> = {
  requested: 'Solicitada',
  approved: 'Aprobada',
  in_transit: 'En tránsito',
  received: 'Recibida',
  resolved: 'Resuelta',
  rejected: 'Rechazada',
};

/** Solicitud de devolución. */
export interface ReturnRequest {
  id: number;
  paymentId?: number;
  userId?: number;
  userName?: string;
  reason?: string;
  status?: string;
  resolution?: string;
  labelUrl?: string;
  evidence?: EvidenceFile[];
  createdAt?: string;
  [key: string]: unknown;
}
